import React, { useState, useEffect } from 'react';
import { Shield, ShieldCheck, ShieldOff, Key, Smartphone, RefreshCw, AlertTriangle, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import MFASetup from './MFASetup';
import api from '../utils/api';

const MFASettings = () => {
  const [mfaStatus, setMfaStatus] = useState(null);
  const [trustedDevices, setTrustedDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [showRegenerate, setShowRegenerate] = useState(false);
  const [disableData, setDisableData] = useState({ password: '', code: '' });
  const [regenerateCode, setRegenerateCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchMFAStatus();
  }, []);

  const fetchMFAStatus = async () => {
    try {
      const [statusRes, devicesRes] = await Promise.all([
        api.get('/auth/mfa/status'),
        api.get('/auth/mfa/trusted-devices').catch(() => ({ data: { devices: [] } }))
      ]);
      setMfaStatus(statusRes.data);
      setTrustedDevices(devicesRes.data.devices || []);
    } catch (err) {
      setError('Failed to load MFA status');
    } finally {
      setLoading(false);
    }
  };

  const handleDisableMFA = async () => {
    if (!disableData.password || !disableData.code) {
      setError('Please enter your password and verification code');
      return;
    }

    setActionLoading(true);
    setError('');
    try {
      await api.post('/auth/mfa/disable', {
        password: disableData.password,
        code: disableData.code
      });
      setSuccess('Two-factor authentication has been disabled');
      setShowDisable(false);
      setDisableData({ password: '', code: '' });
      fetchMFAStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to disable MFA');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (regenerateCode.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }

    setActionLoading(true);
    setError('');
    try {
      const response = await api.post('/auth/mfa/backup-codes', {
        code: regenerateCode
      });
      
      // Show new backup codes
      const codes = response.data.backup_codes;
      const codesText = codes.join('\n');
      
      // Download automatically
      const blob = new Blob([`Athena MFA Backup Codes\n\nGenerated: ${new Date().toISOString()}\n\n${codes.map((c, i) => `${i + 1}. ${c}`).join('\n')}`], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'athena-backup-codes.txt';
      a.click();
      URL.revokeObjectURL(url);

      setSuccess('New backup codes generated and downloaded');
      setShowRegenerate(false);
      setRegenerateCode('');
      fetchMFAStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to regenerate backup codes');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeTrustedDevice = async (deviceId) => {
    try {
      await api.delete(`/auth/mfa/trusted-devices/${deviceId}`);
      setTrustedDevices(prev => prev.filter(d => d.id !== deviceId));
      setSuccess('Device removed from trusted devices');
    } catch (err) {
      setError('Failed to revoke trusted device');
    }
  };

  const handleSetupSuccess = () => {
    setShowSetup(false);
    fetchMFAStatus();
    setSuccess('Two-factor authentication is now enabled!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {mfaStatus?.mfa_enabled ? (
                <div className="p-2 bg-green-100 rounded-lg">
                  <ShieldCheck className="w-6 h-6 text-green-600" />
                </div>
              ) : (
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Shield className="w-6 h-6 text-gray-600" />
                </div>
              )}
              <div>
                <CardTitle>Two-Factor Authentication</CardTitle>
                <CardDescription>
                  {mfaStatus?.mfa_enabled 
                    ? 'Your account is protected with 2FA'
                    : 'Add an extra layer of security to your account'
                  }
                </CardDescription>
              </div>
            </div>
            {!mfaStatus?.mfa_enabled && (
              <Button onClick={() => setShowSetup(true)} className="bg-purple-600 hover:bg-purple-700">
                Enable 2FA
              </Button>
            )}
          </div>
        </CardHeader>

        {mfaStatus?.mfa_enabled && (
          <CardContent className="space-y-4">
            {success && (
              <Alert className="bg-green-50 border-green-200">
                <AlertDescription className="text-green-800">{success}</AlertDescription>
              </Alert>
            )}
            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              {/* Backup Codes Status */}
              <div className="p-4 border rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Key className="w-4 h-4 text-amber-500" />
                  <span className="font-medium">Backup Codes</span>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  {mfaStatus.backup_codes_remaining} of 10 codes remaining
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowRegenerate(true)}
                >
                  Regenerate Codes
                </Button>
              </div>

              {/* Disable MFA */}
              <div className="p-4 border rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldOff className="w-4 h-4 text-red-500" />
                  <span className="font-medium">Disable 2FA</span>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Remove two-factor authentication
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDisable(true)}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  Disable
                </Button>
              </div>
            </div>

            {/* Trusted Devices */}
            {trustedDevices.length > 0 && (
              <div className="pt-4 border-t">
                <div className="flex items-center gap-2 mb-3">
                  <Smartphone className="w-4 h-4 text-purple-500" />
                  <span className="font-medium">Trusted Devices</span>
                </div>
                <div className="space-y-2">
                  {trustedDevices.map((device) => (
                    <div key={device.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium">{device.device_name || 'Unknown Device'}</p>
                        <p className="text-xs text-gray-500">
                          Trusted until {new Date(device.trusted_until).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRevokeTrustedDevice(device.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Org MFA Policy Notice */}
      {mfaStatus?.mfa_required_by_org && !mfaStatus?.mfa_enabled && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            Your organization requires two-factor authentication. 
            {mfaStatus.enforcement_deadline && (
              <> Please enable it by {new Date(mfaStatus.enforcement_deadline).toLocaleDateString()}.</>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Setup Modal */}
      {showSetup && (
        <MFASetup onClose={() => setShowSetup(false)} onSuccess={handleSetupSuccess} />
      )}

      {/* Disable Modal */}
      {showDisable && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-red-600">Disable Two-Factor Authentication</CardTitle>
              <CardDescription>
                This will make your account less secure. Please confirm by entering your password and a verification code.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <label className="text-sm font-medium">Password</label>
                <Input
                  type="password"
                  value={disableData.password}
                  onChange={(e) => setDisableData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter your password"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Verification Code</label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={disableData.code}
                  onChange={(e) => setDisableData(prev => ({ ...prev, code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  placeholder="000000"
                  className="font-mono"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => { setShowDisable(false); setError(''); setDisableData({ password: '', code: '' }); }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDisableMFA}
                  disabled={actionLoading}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Disable 2FA'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Regenerate Backup Codes Modal */}
      {showRegenerate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Regenerate Backup Codes</CardTitle>
              <CardDescription>
                This will invalidate all existing backup codes. Enter a verification code to continue.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <label className="text-sm font-medium">Verification Code</label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={regenerateCode}
                  onChange={(e) => setRegenerateCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="font-mono text-center"
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => { setShowRegenerate(false); setError(''); setRegenerateCode(''); }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRegenerateBackupCodes}
                  disabled={actionLoading || regenerateCode.length !== 6}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Regenerate'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default MFASettings;
